import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { tap, catchError, of } from 'rxjs';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { DocumentoUICamViewModel, TwUnidadInspeccionViewModelExtended } from '../../unidad-inspeccion-datosbasicos.types';
import { UnidadInspeccionService } from '../../unidad-inspeccion.service';
import { DocumentoUiService } from '../../documento-ui.service';

@Component({
    selector: 'app-documento-ui-wrapper',
    templateUrl: './documento-ui-wrapper.component.html',
    styleUrls: ['./documento-ui-wrapper.component.scss'],
})
export class DocumentoUiWrapperComponent extends ComponentBase implements OnInit {
    formGroup: FormGroup;
    documentosUI: DocumentoUICamViewModel[];
    unidadInspeccion: TwUnidadInspeccionViewModelExtended;
    title = { plural: 'Documentos de U.I.', singular: 'Documento' };

    private listUrl = '/campanias/unidadesinspeccion';
    private documentoUICreationUrl = '/campanias/unidadinspeccion/documentos';
    private IdUi: number;
    private deletingSuccessMessage = 'Documento de U.I. eliminado correctamente';
    idUsuarioActual: number | undefined = undefined;

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const idUi: any = params.get('idUi');
        this.IdUi = idUi;
        if (idUi) {
            this.getUnidadInspeccion(idUi);
            this.getListaDocumentosByIdUi(idUi);
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private unidadInspeccionService: UnidadInspeccionService,
        private docUiCamService: DocumentoUiService,
        private navMenuService: NavMenuService,
        private confirmationService: ConfirmationService,
        protected authService: AuthService,
        protected notificationService: NotificationService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.buildForm();
        this.idUsuarioActual = this.loggedInUser?.Id;
    }

    doReload(): void {
        this.getListaDocumentosByIdUi(this.IdUi);
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            Campania: [null],
            Periodo: [null],
            DescripcionComunidad: [null],
            EntidadAuditora: [null],
            TipoRecinto: [null], // Tipo de Solicitud
            Operador: [null],
        });
        this.form = this.formGroup;
    }

    /**
     * Se cargan los datos de la unidad de inspección y sus recintos "PI-A-Ed" asociados
     * Una vez obtenida, establece el valor para la propiedad readonly a partir de la información del usuario y los permisos de edición.
     * Después invoca al servicio de obtención de inspecciones de la U.I.
     * @param id number
     */
    private getUnidadInspeccion(id: number): void {
        this.unidadInspeccionService
            .getUI(id)
            .pipe(
                tap((udInspeccion: TwUnidadInspeccionViewModelExtended) => {
                    // Una UI existente deberá tener al menos un recinto "PI-A-Ed".
                    if (!udInspeccion?.FirstRecinto) {
                        // No debería pasar por aquí, porque cuando se eliminan todos los recintos "PI-A-Ed" de una UI, se elimina además la UI
                        return;
                    }
                    this.unidadInspeccion = udInspeccion;
                    this.navMenuService.updateMenuServiceForUdInspeccion(
                        udInspeccion.IdUi!,
                        udInspeccion.IdEstadoUi!,
                        udInspeccion.FirstRecinto.IdTipoSolicitudCam || 0,
                        udInspeccion.PeriodoCertificaKgs,
                    );
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    private getListaDocumentosByIdUi(idUi: number): void {
        this.docUiCamService
            .getDocumentosByIdUi(idUi)
            .pipe(
                tap((docs: DocumentoUICamViewModel[]) => {
                    this.documentosUI = docs;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Redigirimos al usuario a la pantalla de edición de la UI, para la selección de recintos.
     */
    onAdd(): void {
        this.router.navigate([this.documentoUICreationUrl, this.IdUi, 'create']);
    }

    onDelete(idDocumento: number): void {
        this.confirmationService.confirm({
            message: 'Se va a eliminar el documento, ¿desea continuar?',
            accept: () => {
                this.delete(idDocumento);
            },
        });
    }

    private delete(idDocumento: number): void {
        const arrIdsDocs = [idDocumento];
        this.docUiCamService
            .deleteDocumentos(arrIdsDocs)
            .pipe(
                tap(() => {
                    this.addSuccessMessage(this.deletingSuccessMessage);
                    this.doReload();
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    get hasDeletingAccess(): boolean {
        if (!this.IdUi || !this.unidadInspeccion) return false;
        const hasAccess = this.loggedInUser!.getAction(EnumCodigosTwTcAcciones.REMOVE_DOC_UI, this.unidadInspeccion.IdEstadoUi).Editable;
        return hasAccess;
    }

    get hasAddingAccess(): boolean {
        if (!this.IdUi || !this.unidadInspeccion) return false;
        const hasAccess = this.loggedInUser!.getAction(EnumCodigosTwTcAcciones.ADD_DOC_UI, this.unidadInspeccion.IdEstadoUi).Editable;
        return hasAccess;
    }

    get hasViewingAccess(): boolean {
        if (!this.IdUi || !this.unidadInspeccion) return false;

        const hasAccess = this.loggedInUser!.getAction(
            EnumCodigosTwTcAcciones.CONS_DOC_UI,
            this.unidadInspeccion.IdEstadoUi,
            this.unidadInspeccion?.FirstRecinto?.IdTipoSolicitudCam,
        ).Editable;

        return hasAccess && this.unidadInspeccion?.FirstRecinto?.PeriodoEnPlazoVigente;
    }

    get actionNotAllowed(): boolean {
        return !this.unidadInspeccion?.FirstRecinto?.PeriodoEnPlazoVigente;
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    onView(idDocumento: number): void {
        this.router.navigate([this.documentoUICreationUrl, this.IdUi, 'detail', idDocumento]);
    }
}
